import { format } from "date-fns";
import { FC, ReactNode } from "react";
import {
  AssetLabel,
  assetLabel,
  Decimal,
  IconDenom,
  useTranslation,
} from "rujira.ui";
import { TradeSubscriptionsFinRangeFragment$data } from "../../__generated__/TradeSubscriptionsFinRangeFragment.graphql";
import { PairLink, useTradeUrl } from "../Orders/Common";
import { useRangeFormat } from "../Orders/shared/format";
import {
  InRange,
  OutOfRange,
  RangePip,
  SimpleRange,
} from "../Orders/shared/RangeBar";
import { OrderRow, OrderRowDetail } from "./OrderRow";

export const RangeOrderRow: FC<{
  p: TradeSubscriptionsFinRangeFragment$data;
  showDetails: boolean;
  actions: ReactNode;
}> = ({ p, showDetails, actions }) => {
  const { t } = useTranslation();
  const {
    notAvailable,
    formatAge,
    formatApr,
    formatPct,
    formatRatio,
    formatSkew,
  } = useRangeFormat();
  const tradeTo = useTradeUrl(p.pair.assetBase, p.pair.assetQuote);
  const base = p.pair.assetBase.metadata;
  const quote = p.pair.assetQuote.metadata;
  const baseLabel = assetLabel(p.pair.assetBase);
  const quoteLabel = assetLabel(p.pair.assetQuote);
  const closed = p.base + p.quote + p.feesBase + p.feesQuote === 0n;
  const centerPrice = p.pair.book?.center ?? 0n;
  const analytics = p.analytics;

  const qd = (amount: bigint) => (
    <Decimal
      subscript
      amount={amount}
      decimals={quote.decimals}
      round={4}
      symbol={quote.symbol}
      symbolClassName="decimal__symbol--small color-grey condensed fw-500"
      clip={true}
    />
  );

  const stat = (v: string) => <span className="mono">{v}</span>;

  // MOIC and DPI are narrow enough to share one cell of the details grid.
  const ratio = (label: string, v?: bigint | null) => (
    <span className="order-row__detail">
      <span className="mobile-card__label">{label}</span>
      <span className="order-row__value">{stat(formatRatio(v))}</span>
    </span>
  );

  const label = (asset: typeof p.pair.assetBase) => (
    <AssetLabel
      asset={asset}
      Container={({ children }) => (
        <small className="color-grey fs-11">{children}</small>
      )}
    />
  );

  const range = closed ? (
    <SimpleRange low={p.low} high={p.high} />
  ) : p.inRange ? (
    <InRange current={centerPrice} low={p.low} high={p.high} />
  ) : (
    <OutOfRange current={centerPrice} low={p.low} high={p.high} />
  );

  const currentValue = qd(analytics?.unrealizedInvestment || 0n);

  const apr = (
    <span className="mono color-teal">
      {p.fee === 0n ? t("common:compounding") : formatApr(analytics?.apr)}
    </span>
  );

  const details: OrderRowDetail[] = [
    { value: range, full: true },
    {
      label: "Created on",
      value: analytics?.firstDepositDate
        ? stat(format(analytics.firstDepositDate, "dd-MMM-yy"))
        : stat(notAvailable),
    },
    {
      label: "Age",
      value: stat(formatAge(analytics?.weightedAverageInvestmentAgeDays)),
    },
    { label: t("common:apr"), value: apr },
    { label: "Spread", value: stat(formatPct(p.spread)) },
    { label: "Fee", value: stat(formatPct(p.fee)) },
    { label: "Skew", value: stat(formatSkew(p.skew)) },
    {
      label: "Total Invested",
      value: analytics?.totalInvested
        ? qd(analytics.totalInvested)
        : stat(notAvailable),
    },
    { label: "Current Value", value: currentValue },
    {
      value: (
        <span className="order-row__split">
          {ratio("MOIC", analytics?.moic)}
          {ratio("DPI", analytics?.dpi)}
        </span>
      ),
    },
    ...(showDetails
      ? [
          { label: "Withdrawn", value: qd(analytics?.totalWithdrawn || 0n) },
          { label: "Claimed Yield", value: qd(analytics?.realizedYield || 0n) },
          {
            label: "Unclaimed Yield",
            value: qd(analytics?.unrealizedYield || 0n),
          },
        ]
      : []),
  ];

  return (
    <OrderRow
      icons={
        <>
          <IconDenom denom={base.symbol} />
          <IconDenom denom={quote.symbol} />
        </>
      }
      title={
        <>
          {label(p.pair.assetBase)}
          <span className="color-grey">/</span>
          {label(p.pair.assetQuote)}
        </>
      }
      label={`${baseLabel}/${quoteLabel}`}
      aside={
        <RangePip
          current={centerPrice}
          low={p.low}
          high={p.high}
          inRange={p.inRange}
          closed={closed}
        />
      }
      primary={currentValue}
      secondary={
        p.fee === 0n ? (
          apr
        ) : (
          <>
            <span className="mobile-card__label mr-0.5">{t("common:apr")}</span>
            {apr}
          </>
        )
      }
      details={details}
      columns={3}
      actionsLastColumn
      actions={
        <>
          <PairLink to={tradeTo} label={t("viewPair")} />
          {actions}
        </>
      }
    />
  );
};
